import React from 'react';

import { Box, Typography } from '@mui/material';

import { fontFamily } from '../../../theme/tokens';
import { IidfLogo } from '../IidfLogo';

import type { AuthPageTitleProperties } from './AuthPageTitle.types';

/**
 * Кегль слова приложения. Объявлен один раз: от него считается высота
 * разделителя, и разъехаться они не должны.
 */
const APP_NAME_FONT_SIZE = { xs: '1.5rem', md: '1.75rem' } as const;

/**
 * Шапка auth-экранов: «логотип ФРИИ | слово приложения» и подзаголовок под ней.
 *
 * ─── Откуда взялись значения ────────────────────────────────────────────────
 *
 * Из замера эталона — `https://deals.iidf.ru/auth/login`, 27.09.2026
 * (AF-574/AF-577). Эталон назначен владельцем; числа сняты
 * `getComputedStyle` + `getBoundingClientRect` по реальным элементам живой
 * страницы, а не переписаны из кода deals.
 *
 *     ряд          flex, align center, gap 12px, margin-bottom 24px, высота 36px
 *     логотип      height 28/36, object-fit contain, display block
 *                  (рисует общий `IidfLogo` — AF-580)
 *     разделитель  «|» 28px, line-height 1, weight 300, text.muted
 *                  (заменён на черту во всю высоту ряда — см. ниже)
 *     слово        28px, line-height 1, weight 700, primary.main, моношрифт
 *     подзаголовок h2, 22px, weight 700, letter-spacing -0.03em
 *
 * ─── Почему `line-height: 1` — не косметика ─────────────────────────────────
 *
 * Это ровно то, из-за чего шапка расходилась на 6 пикселей. У applications и
 * analyst его не было, применялся `body1` (1.5), и 28 × 1.5 = 42 — против 36 у
 * эталона. Ряд тянулся не логотипом, а строчным боксом текста.
 *
 * ─── Почему `color: 'inherit'` на ссылке обязателен ─────────────────────────
 *
 * Без него `<a>` получает браузерный дефолт `rgb(0, 0, 238)`, и он протекает в
 * потомков, у которых собственный цвет не разрешился. Так «|» на analyst и
 * стал синим: `text.muted` не доезжал до палитры (AF-576), глиф наследовал —
 * и наследовать ему было нечего, кроме синевы ссылки. Палитра починена
 * отдельно, но `inherit` остаётся: он закрывает весь класс, а не тот случай.
 *
 * ─── Где эталон поправлен, а не повторён ────────────────────────────────────
 *
 * Разделитель. В эталоне это глиф «|», и он стоит ниже букв и короче их: в
 * Inter вертикальная черта свисает под базовую линию, а рядом с логотипом
 * высотой 36px выглядит мелкой. Правка по замечанию владельца 27.09.2026 —
 * черта рисуется и идёт во всю высоту ряда. Остальное повторяет эталон.
 *
 * ─── Что параметризуется, а что нет ─────────────────────────────────────────
 *
 * Различаются законно только слово, переход по логотипу и префикс
 * `data-testid`. Оформление не параметризуется вовсе — в этом и смысл подъёма:
 * три копии разошлись именно потому, что каждая могла решать сама.
 *
 * Ссылка сохраняется там, где она была (решение владельца, 27.09.2026):
 * «ровно как на deals» сказано про вид, не про поведение. Поэтому deals
 * остаётся `<div>`, а applications и analyst — ссылками, неотличимыми от него
 * на вид.
 */
export const AuthPageTitle: React.FC<AuthPageTitleProperties> = ({
  appName,
  dataTest = 'auth_page_title',
  children,
  logoLink,
}) => {
  const { component: linkComponent, ...linkProperties } = logoLink ?? {};

  return (
    <Box>
      <Box
        {...(logoLink ? { component: linkComponent, ...linkProperties } : {})}
        data-testid={logoLink ? `${dataTest}_logo_link` : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 3,
          color: 'inherit',
          textDecoration: 'none',
          ...(logoLink && {
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 },
          }),
        }}
      >
        <IidfLogo
          height={{ xs: 28, md: 36 }}
          dataTest={`${dataTest}_logo`}
        />
        <Box
          data-testid={`${dataTest}_separator`}
          sx={{
            /**
             * Рисованная черта, а не глиф «|», и во всю высоту ряда.
             *
             * Глиф не годился дважды. Он стоял ниже букв — в Inter
             * вертикальная черта свисает под базовую линию, — и он был
             * заметно короче соседей. Замер витрины deals 27.09.2026, отсчёт
             * от верха ряда высотой 36px:
             *
             *     логотип «ФРИИ»   ink   0.00 → 36.00
             *     слово «Сделки»   ink   6.50 → 27.50
             *     глиф «|»               7.28px шириной, короче обоих
             *
             * Разделитель обязан идти по самой высокой букве ряда, а она в
             * логотипе и занимает все 36px.
             *
             * ─── Почему `stretch`, а не доля от кегля ─────────────────────
             *
             * Здесь стояла высота `calc(кегль × 0.73)` — прописная высота
             * слова. Она давала 20.44px против 36px у логотипа, то есть черта
             * не дотягивалась до самой высокой буквы. И доля была бы вторым
             * источником геометрии: ряд уже знает свою высоту, потому что её
             * задаёт логотип.
             *
             * `stretch` берёт её у ряда и тем самым следует за логотипом сам —
             * при смене его размера подкручивать нечего.
             *
             * ─── Толщина: 3px, и почему не 2 и не 4 ──────────────────────
             *
             * Замер штрихов в витрине deals 27.09.2026 (медиана непрерывных
             * серий чернил по горизонтали на середине высоты):
             *
             *     логотип «ФРИИ»   5.74px  (22px в оригинале × масштаб 0.261)
             *     слово «Сделки»   4.00px  (JetBrains Mono 700, кегль 28)
             *
             * 2px, стоявшие здесь до того, — вдвое тоньше слова и втрое тоньше
             * логотипа: между двумя жирными знаками это читается как волосяная
             * линия, а не как разделитель.
             *
             * 4px сравняли бы черту со штрихом слова, и она перестала бы быть
             * подчинённой — разделитель не должен спорить с тем, что разделяет.
             * 3px оставляют её легче обоих соседей и при этом видимой.
             * Цветом она уже отступает: `text.muted` светлее и логотипа, и
             * слова.
             */
            alignSelf: 'stretch',
            width: '3px',
            borderRadius: '1.5px',
            bgcolor: 'text.muted',
            flexShrink: 0,
          }}
        />
        <Typography
          component='span'
          sx={{
            fontFamily: fontFamily.mono,
            fontWeight: 700,
            fontSize: APP_NAME_FONT_SIZE,
            color: 'primary.main',
            lineHeight: 1,
          }}
        >
          {appName}
        </Typography>
      </Box>
      <Typography
        data-testid={dataTest}
        variant='h2'
        sx={{
          fontSize: { xs: '1.25rem', md: '1.375rem' },
          fontWeight: 700,
          /**
           * Задано явно, хотя у варианта `h2` своё значение, — потому что оно
           * у приложений разное: applications и analyst накрывают ВСЕ
           * заголовки собственной шкалой `lineHeight: 1.15`, deals оставляет
           * дефолт MUI 1.2. Замер в витринах 27.09.2026: 25.3px против
           * 26.4px при одном и том же `font-size: 22px`.
           *
           * Чинить это в темах значило бы переоформить каждый заголовок обоих
           * приложений ради одной строки на auth-экране. Компонент отвечает за
           * своё оформление сам — так же, как `lineHeight: 1` у частей ряда
           * выше.
           */
          lineHeight: 1.2,
          letterSpacing: '-0.03em',
          color: 'text.primary',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};
